import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface AlertOptions {
  title?: string;
  message: string;
  okLabel?: string;
  danger?: boolean;
}

interface PromptConfirmOptions {
  title?: string;
  message: string;
  /** The exact text the user must type to enable the confirm button. */
  matchText: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface PromptTextOptions {
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  /** If true, the confirm button stays disabled until the input is non-empty. Defaults to false — mirrors window.prompt(), where only Cancel returns null. */
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

type DialogState =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'alert'; options: AlertOptions; resolve: (value: void) => void }
  | { kind: 'prompt-confirm'; options: PromptConfirmOptions; resolve: (value: boolean) => void; input: string }
  | { kind: 'prompt-text'; options: PromptTextOptions; resolve: (value: string | null) => void; input: string }
  | null;

interface ModalContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions | string) => Promise<void>;
  promptConfirm: (options: PromptConfirmOptions) => Promise<boolean>;
  /** Replaces window.prompt(). Resolves the typed string, or null if cancelled. */
  promptText: (options: PromptTextOptions) => Promise<string | null>;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setDialog({ kind: 'confirm', options, resolve });
    });
  }, []);

  const alert = useCallback((options: AlertOptions | string) => {
    const normalized = typeof options === 'string' ? { message: options } : options;
    return new Promise<void>((resolve) => {
      setDialog({ kind: 'alert', options: normalized, resolve });
    });
  }, []);

  const promptConfirm = useCallback((options: PromptConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setDialog({ kind: 'prompt-confirm', options, resolve, input: '' });
    });
  }, []);

  const promptText = useCallback((options: PromptTextOptions) => {
    return new Promise<string | null>((resolve) => {
      setDialog({ kind: 'prompt-text', options, resolve, input: options.defaultValue ?? '' });
    });
  }, []);

  const close = useCallback(
    (result: boolean) => {
      if (!dialog) return;
      if (dialog.kind === 'alert') dialog.resolve();
      else if (dialog.kind === 'prompt-text') dialog.resolve(result ? dialog.input : null);
      else dialog.resolve(result);
      setDialog(null);
    },
    [dialog],
  );

  const value = useMemo(
    () => ({ confirm, alert, promptConfirm, promptText }),
    [confirm, alert, promptConfirm, promptText],
  );

  const promptMatched = dialog?.kind === 'prompt-confirm' && dialog.input === dialog.options.matchText;
  const promptTextValid =
    dialog?.kind === 'prompt-text' && (!dialog.options.required || dialog.input.trim().length > 0);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {dialog && (
        <div className="modal" role="presentation" onClick={() => close(false)}>
          <div
            className="modal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            aria-describedby="modal-message"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Pages v5 structure: modal-content > header / body / footer
                (bundle/demo/html/simply_white/modals.html:501). Kept as one
                component so every useModal() caller gets it for free. */}
            <div className="modal-content">
            {dialog.options.title && (
              <div className="modal-header">
                <h5 id="modal-title">{dialog.options.title}</h5>
              </div>
            )}
            <div className="modal-body">
            <p id="modal-message">{dialog.options.message}</p>
            {dialog.kind === 'prompt-confirm' && (
              <input
                type="text"
                className="modal-prompt-input"
                autoFocus
                value={dialog.input}
                placeholder={dialog.options.matchText}
                onChange={(e) => setDialog({ ...dialog, input: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && dialog.input === dialog.options.matchText) close(true);
                }}
              />
            )}
            {dialog.kind === 'prompt-text' && (
              <input
                type="text"
                className="modal-prompt-input"
                autoFocus
                value={dialog.input}
                placeholder={dialog.options.placeholder}
                onChange={(e) => setDialog({ ...dialog, input: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (!dialog.options.required || dialog.input.trim())) close(true);
                }}
              />
            )}
            </div>
            <div className="modal-footer">
              {dialog.kind !== 'alert' && (
                <button type="button" className="btn btn-secondary" onClick={() => close(false)} autoFocus={dialog.kind === 'confirm'}>
                  {dialog.options.cancelLabel ?? 'Cancel'}
                </button>
              )}
              <button
                type="button"
                className={dialog.kind !== 'prompt-text' && dialog.options.danger ? 'btn btn-danger' : 'btn btn-primary'}
                onClick={() => close(true)}
                disabled={
                  (dialog.kind === 'prompt-confirm' && !promptMatched) ||
                  (dialog.kind === 'prompt-text' && !promptTextValid)
                }
                autoFocus={dialog.kind === 'alert'}
              >
                {dialog.kind === 'alert'
                  ? (dialog.options.okLabel ?? 'OK')
                  : (dialog.options.confirmLabel ?? 'Confirm')}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
}
